import { useCallback, useEffect, useState } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button,
  Spinner,
} from "@heroui/react";
import { Checkmark, Close } from "assets/icons";
import Menu from "./menu";
import { AvailableRenovationLabel } from "shared/types/renovation";
import { useSession, useUser } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import {
  Acquisition,
  AcquisitionPhase,
  DealType,
} from "shared/types/acquisition";
import { useNavigate } from "react-router-dom";
import ButtonNew from "components/buttonNew/buttonNew";
import { debounce } from "lodash";
import { v4 as uuidv4 } from "uuid";

export interface BuildingProps {
  label?: string;
  building: {
    id: number;
    location: { description: string; placeId: string };
    description: string;
    units: {
      resUnits?: number;
      retailUnits?: number;
      officeUnits?: number;
      indoorParking?: number;
      outdoorParking?: number;
      bikeStalls?: number;
      storages?: number;
    };
    buildingAlreadyRenovatedLabels?: AvailableRenovationLabel[];
    buildingToRenovateLabels?: AvailableRenovationLabel[];
  };
  updateBuildingDetails: (field: string, value: any) => void;
}

interface AddAcquisitionProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  existingAcquisition?: Acquisition | null;
  onClose?: () => void;
  initialPhase?: AcquisitionPhase;
}

const AddAcquisition = ({ isOpen, onOpenChange, existingAcquisition, onClose, initialPhase }: AddAcquisitionProps) => {
  const { user } = useUser();
  const { session } = useSession();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const [acquisitionFile, setAcquisitionFile] = useState<Acquisition>({
    dealType: DealType.UNKNOWN,
    name: "",
    grossAskingPrice: 0,
    description: "",
    grossActualRent: 0,
    saleReason: "",
    acquisitionSource: null,
    acquisitionSourceUrl: "",
    intermediary: null,
    isEmailImport: false,
    emailFrom: "",
    emailReceivedAt: "",
    owner: null,
    acquisitionDealAppreciation: "",
    yallyResponsibleUser: user,
    acquisitionProReasons: [],
    acquisitionConReasons: [],
    acquisitionPhase: initialPhase || AcquisitionPhase.INBOX,
    buildings: [
      {
        id: "",
        tempId: uuidv4(),
        location: "",
        buildingAcquisitionInfo: {
          // Every field under this object has to start with acquisitionFile, else it will get misplaced
          acquisitionFileNumResidentialUnits: 0,
          acquisitionFileNumRetailUnits: 0,
          acquisitionFileNumOfficeUnits: 0,
          acquisitionFileNumParkingsInside: 0,
          acquisitionFileNumParkingsOutside: 0,
          acquisitionFileNumBicycleSheds: 0,
          acquisitionFileNumStorages: 0,
          acquisitionFileBuildingDescription: "",
        },
        buildingAlreadyRenovated: [],
        buildingToRenovate: [],
        owner: false,
      },
    ],
  });

  useEffect(() => {
    if (existingAcquisition) {
      setAcquisitionFile({
        ...existingAcquisition,
        name: existingAcquisition.emailSubject || existingAcquisition.name,
        yallyResponsibleUser: user,
        buildings: existingAcquisition.buildings || [{
          id: "",
          tempId: uuidv4(),
          location: "",
          buildingAcquisitionInfo: {
            acquisitionFileNumResidentialUnits: 0,
            acquisitionFileNumRetailUnits: 0,
            acquisitionFileNumOfficeUnits: 0,
            acquisitionFileNumParkingsInside: 0,
            acquisitionFileNumParkingsOutside: 0,
            acquisitionFileNumBicycleSheds: 0,
            acquisitionFileNumStorages: 0,
            acquisitionFileBuildingDescription: "",
          },
          buildingAlreadyRenovated: [],
          buildingToRenovate: [],
          owner: false,
        }],
        isEmailImport: existingAcquisition.isEmailImport,
        emailFrom: existingAcquisition.emailFrom,
        emailSubject: existingAcquisition.emailSubject,
        emailReceivedAt: existingAcquisition.emailReceivedAt,
        description: "",
      });
    } else if (initialPhase) {
      // Update phase when initialPhase changes for new acquisitions
      setAcquisitionFile(prev => ({
        ...prev,
        acquisitionPhase: initialPhase
      }));
    }
  }, [existingAcquisition, user, initialPhase]);

  const updateAcquisitionFile = useCallback((updates: any) => {
    setAcquisitionFile((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleSave = async () => {
    if (isLoading) return;

    if(!acquisitionFile.grossAskingPrice || isNaN(acquisitionFile.grossAskingPrice)) {
      acquisitionFile.grossAskingPrice = 0;
      toast.info("Vraagprijs moet een getal zijn, door het systeem ingesteld op 0.");
    }

    if (!acquisitionFile.name) {
      return toast.error("Naam voor acquisitiedossier vereist");
    }

    if (acquisitionFile.name.length > 40) {
      return toast.error("Naam voor acquisitiedossier mag niet langer zijn dan 40 tekens");
    }

    if (!acquisitionFile.acquisitionDealAppreciation) {
      return toast.error("Geef aan waarom deze deal interessant is.");
    }

    if (!acquisitionFile.buildings || acquisitionFile.buildings.length == 0) {
      return toast.error("Voeg gebouwen toe aan het acquisitiedossier.");
    }
    for (let building of acquisitionFile.buildings) {
      if (
        !building.location ||
        !building.location.description ||
        !building.location.placeId
      ) {
        return toast.error("Kies voor elk gebouw het juiste adres.");
      }
    }

    setIsLoading(true);

    try {
      const endpoint = existingAcquisition 
        ? `${API_URL}/acquisitions/${existingAcquisition.id}/complete`
        : `${API_URL}/acquisitions/new`;

      const res = await fetchWithAuth(
        endpoint,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(acquisitionFile),
        },
        session,
      );

      if (!res.ok) {
        throw new Error("Server error when saving acquisition");
      }

      const savedAcquisition = await res.json();
      toast.success(existingAcquisition ? "Acquisitie succesvol bijgewerkt!" : "Acquisitie succesvol aangemaakt!");
      setIsLoading(false);
      
      // Reset state and close modal
      setAcquisitionFile({
        dealType: DealType.UNKNOWN,
        name: "",
        grossAskingPrice: 0,
        description: "",
        grossActualRent: 0,
        saleReason: "",
        acquisitionSource: null,
        isEmailImport: false,
        emailFrom: null,
        emailReceivedAt: null,
        acquisitionSourceUrl: "",
        intermediary: null,
        owner: null,
        acquisitionDealAppreciation: "",
        yallyResponsibleUser: user,
        acquisitionProReasons: [],
        acquisitionConReasons: [],
        acquisitionPhase: initialPhase || AcquisitionPhase.INBOX,
        buildings: [
          {
            id: "",
            tempId: uuidv4(),
            location: "",
            buildingAcquisitionInfo: {
              acquisitionFileNumResidentialUnits: 0,
              acquisitionFileNumRetailUnits: 0,
              acquisitionFileNumOfficeUnits: 0,
              acquisitionFileNumParkingsInside: 0,
              acquisitionFileNumParkingsOutside: 0,
              acquisitionFileNumBicycleSheds: 0,
              acquisitionFileNumStorages: 0,
              acquisitionFileBuildingDescription: "",
            },
            buildingAlreadyRenovated: [],
            buildingToRenovate: [],
            owner: false,
          },
        ],
      });
      
      if (onClose) {
        onClose();
      }
      
      navigate(`/acquisitions/${savedAcquisition.id}/detail`);
    } catch (e) {
      console.error(e);
      setIsLoading(false);
      toast.error(
        `Er is een fout opgetreden bij het ${existingAcquisition ? 'bijwerken' : 'aanmaken'} van de acquisitie: ` + e,
      );
    }
  };

  // Debounce the save function to prevent multiple rapid submissions
  const debouncedSave = debounce(handleSave, 300);

  return (
    <>
      {isLoading && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-white bg-opacity-50 text-neutral-primary/90 backdrop-blur-md">
          <Spinner color="success" size="lg" />
          <p>
            Even geduld, ik ben het acquisitiedossier, de gebouwen, de
            sharepointstructuur en de excel aan het aanmaken...
          </p>
        </div>
      )}
      <Modal
        scrollBehavior="inside"
        hideCloseButton
        size="full"
        className="max-w-212.5 rounded-md!"
        isOpen={!isLoading && isOpen}
        onOpenChange={onOpenChange}
      >
        <ModalContent>
          <ModalHeader className="flex items-center justify-between font-normal">
            <p>Nieuwe acquisitie toevoegen</p>
            <div className="flex items-center gap-2">
              {!isLoading && (
                <ButtonNew
                  variant="primaryMoss"
                  onClick={debouncedSave}
                  icon={<Checkmark />}
                  label="Bewaren"
                  isDisabled={isLoading}
                />
              )}

              {isLoading && (
                <ButtonNew
                  variant="primaryMoss"
                  icon={<Spinner />}
                  label="Aan het bewaren..."
                />
              )}
              <Button
                onClick={() => onOpenChange(false)}
                isIconOnly
                variant="light"
                className="text-neutral-secondary-mid"
              >
                <Close />
              </Button>
            </div>
          </ModalHeader>
          <ModalBody>
            <Menu
              updateAcquisitionFile={updateAcquisitionFile}
              acquisitionFile={acquisitionFile}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AddAcquisition;
